import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { RemoteDiagnosticsVddGenericStructure, SetNewValueObject } from '@vi-iot/client';
import { UnitsStore } from '@store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DiagnosticsFormGroupService } from '../diagnostics-form-group.service';
import { SemanticValues } from '../../diagnostics-constants';
import { InputValuePipe } from '../../../../../../shared/pipes/input-value/input-value.pipe';
import { convertCelsiusToFahrenheit } from '../../../../../../shared/common/units';
import { SUFFIX } from '../../../../../../shared/common/units';
import {
  getCelsiusFromFahrenheit,
  getFahrenheitCeilValueFromCelsius,
  getFahrenheitFloorValueFromCelsius,
} from '../../../../../../features/temperature/converter/converter';

@Component({
  selector: 'app-vdd-number-edit',
  templateUrl: './vdd-number-edit.component.html',
  styleUrls: ['./vdd-number-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VddNumberEditComponent implements OnInit, OnDestroy {
  @Input() matchedValue: RemoteDiagnosticsVddGenericStructure;
  @Input() diagnosticsFormGroupService: DiagnosticsFormGroupService;
  @Input() metadataValues: RemoteDiagnosticsVddGenericStructure[];
  @Output() setNewValue = new EventEmitter<SetNewValueObject>();
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  isCelsius: boolean;
  numberFormControl: FormControl<string>;
  maxValue: number;
  minValue: number;
  precision = 0;
  pattern = '';
  step = 1;

  constructor(private inputValuePipe: InputValuePipe, private unitsStore: UnitsStore) {}

  ngOnInit(): void {
    this.unitsStore.isTemperatureMetric$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isCelsius) => {
        this.isCelsius = isCelsius;
        this.setConstraints();
        this.setPrecision();
        this.setPattern();

        if (!isCelsius && this.matchedValue.unit === SUFFIX.celsius) {
          this.minValue = getFahrenheitCeilValueFromCelsius(this.minValue, this.precision);
          this.maxValue = getFahrenheitFloorValueFromCelsius(this.maxValue, this.precision);
          const fahrenheitValue = convertCelsiusToFahrenheit(
            Number(this.matchedValue.value)
          ).toFixed(this.precision);
          this.matchedValue.value = fahrenheitValue === '-0.0' ? '0.0' : fahrenheitValue;
          this.matchedValue.unit = SUFFIX.fahrenheit;
        }

        this.numberFormControl = new FormControl<string>(this.matchedValue.value, [
          Validators.min(this.minValue),
          Validators.max(this.maxValue),
          Validators.required,
          Validators.pattern(this.pattern),
        ]);
        this.diagnosticsFormGroupService.addFormControl(
          `${this.matchedValue.path}`,
          this.numberFormControl
        );
        this.diagnosticsFormGroupService.formGroup.markAllAsTouched();
      });
  }

  valueChange(event: Event): void {
    let value = this.inputValuePipe.transform(event);
    if (this.precision > 0) {
      value = (+value).toFixed(this.precision);
    } else {
      value = (+value).toFixed(0);
    }

    if (!this.isCelsius && this.matchedValue.unit === SUFFIX.fahrenheit) {
      value = getCelsiusFromFahrenheit(Number(value), this.precision).toString();
    }

    this.setNewValue.emit({
      path: this.matchedValue?.path,
      newValue: value.toString(),
    });
  }

  ngOnDestroy(): void {
    this.diagnosticsFormGroupService.removeFormControl(`${this.matchedValue.path}`);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setConstraints(): void {
    const minFromMetadata = this.getConstraint(this.matchedValue.name, SemanticValues.MIN);
    const maxFromMetadata = this.getConstraint(this.matchedValue.name, SemanticValues.MAX);

    if (minFromMetadata && maxFromMetadata) {
      this.minValue = Number(minFromMetadata);
      this.maxValue = Number(maxFromMetadata);

      return;
    }

    const constraints = this.matchedValue.constraints;
    if (constraints?.length) {
      this.minValue = +constraints.slice(constraints.indexOf('[') + 1, constraints.indexOf(':'));
      this.maxValue = +constraints.slice(constraints.indexOf(':') + 1, constraints.indexOf(']'));
    }
  }

  private setPrecision(): void {
    if (!isNaN(Number(this.matchedValue?.precision)) && Number(this.matchedValue?.precision) > 0) {
      this.precision = Number(this.matchedValue?.precision);
      this.step = 1 / Math.pow(10, this.precision);
    } else {
      if (this.matchedValue.value.toString().indexOf('.') === -1) {
        this.step = 1;
        this.minValue = Math.ceil(this.minValue);
        this.maxValue = Math.floor(this.maxValue);
      } else {
        const stringValue = this.matchedValue.value.toString();
        this.precision = stringValue.length - 1 - stringValue.indexOf('.');
        this.step = 1 / Math.pow(10, this.precision);
      }
    }
  }

  private setPattern(): void {
    this.pattern =
      this.precision <= 0
        ? '^(\\\\+|-)?[0-9]+$'
        : `^(\\\\+|-)?[0-9]+(\\.[0-9]{1,${this.precision}})?$`;
  }

  private getConstraint(vddName: string, semantic: SemanticValues): string {
    return this.metadataValues?.find((m) => m.path?.includes(vddName) && m.semantic === semantic)
      ?.value;
  }
}
